import { useDataProvider, useInvalidate } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import type { MediaType } from "@/components/forms/schemas/media";
import { Sentry } from "@/lib/sentry";
import type { EventGroup, EventGroupWithEvents } from "@/types/event-group";

/** Cache-key root. A page's event-group queries must sit under it, or these writes will not refresh them. */
export const EVENT_GROUPS_QUERY_ROOT = "event-groups";

/** The most ids one membership call may carry; the admin API answers more with a 400. */
export const MAX_IDS_PER_MEMBERSHIP_CALL = 490;

/**
 * How long after a write the group lists are marked stale once more. The lists read the Typesense
 * copy, which a write reaches a moment after Firestore does, so the first refetch can still see the
 * old group.
 */
export const SEARCH_SYNC_LAG_MS = 3000;

/**
 * A write's answer. `ok: false` carries the sentence to show: the admin API's 400 `message` as it is,
 * or a plain sentence for a refusal the repository words itself. Any other failure is thrown and shown by the page.
 */
export type GroupWriteResult = { ok: true } | { ok: false; message: string };

export type EventGroupInput = {
  name: string;
  about?: string;
  media: MediaType;
};
export type EventGroupUpdateInput = EventGroupInput & { id: string };

/** The admin API's 400 is `{isSuccess: false, message}`. Returns that message, or null for any other failure. */
function adminRefusal(error: unknown): string | null {
  if (!isAxiosError(error) || !error.response || error.response.status !== 400)
    return null;
  const message = (error.response.data as { message?: unknown } | undefined)
    ?.message;
  return typeof message === "string" ? message : null;
}

function breadcrumb(operation: string, data?: Record<string, unknown>): void {
  Sentry.addBreadcrumb({
    category: `event-groups.${operation}`,
    message: `Event group ${operation}`,
    level: "info",
    data,
  });
}

/**
 * Every call an event-group page makes, in one place. The admin API is the default provider:
 * `getOne`, `create`, `update` and `deleteOne` map onto the routes, and `custom` carries only the
 * membership calls. The list is not here: the pages read it through the default provider's `getList`
 * over `/event-groups/explore`. A Tina group (one with an `externalKey`) is never deleted from here.
 *
 * Refused writes return `{ok: false, message}`; every other failure is thrown.
 */
export const useEventGroupRepository = () => {
  const dataProvider = useDataProvider();
  const admin = dataProvider();
  const invalidate = useInvalidate();
  const queryClient = useQueryClient();

  /**
   * Marks the group lists stale, and the group's own page when its id is known. A write that changes
   * a group's members or what a member event shows of it (`membershipChanged`) also marks the events
   * list, because each event carries its group. Refine's `detail` key needs the id, so it is not
   * sent without one. The group lists are marked stale a second time `SEARCH_SYNC_LAG_MS` later,
   * because the search copy trails the write.
   */
  function invalidateGroups(groupId?: string, membershipChanged = false): void {
    void invalidate({
      dataProviderName: "default",
      resource: "event-groups",
      invalidates: groupId ? ["list", "detail"] : ["list"],
      id: groupId,
    });
    void queryClient.invalidateQueries({ queryKey: [EVENT_GROUPS_QUERY_ROOT] });
    setTimeout(() => {
      void invalidate({
        dataProviderName: "default",
        resource: "event-groups",
        invalidates: ["list"],
      });
    }, SEARCH_SYNC_LAG_MS);
    if (membershipChanged) {
      void invalidate({
        dataProviderName: "default",
        resource: "events",
        invalidates: ["list"],
      });
    }
  }

  /** The group and its member events. A 404 throws; the page turns it into null. */
  async function get(id: string): Promise<EventGroupWithEvents> {
    // The default provider's `getOne` answers the body itself, `{group, events}`, not `{data}`.
    const body = await admin.getOne!({ resource: "event-groups", id });
    return body as unknown as EventGroupWithEvents;
  }

  /** Errors throw; the page shows the admin API's 400 `message` through `toHumanErrorMessage`. */
  async function create(input: EventGroupInput): Promise<EventGroup> {
    breadcrumb("create");
    // The default provider's `create` answers the body itself, not `{data}`.
    const body = await admin.create!({
      resource: "event-groups",
      variables: input,
    });
    invalidateGroups();
    return body as unknown as EventGroup;
  }

  /** Errors throw, as `create` does. The body carries `id`, which the PUT route reads. */
  async function update(input: EventGroupUpdateInput): Promise<EventGroup> {
    breadcrumb("update", { id: input.id });
    const response = await admin.update!({
      resource: "event-groups",
      id: input.id,
      variables: input,
    });
    // Each member event carries its group, so a rename changes what the events list shows.
    invalidateGroups(input.id, true);
    return response.data as unknown as EventGroup;
  }

  /**
   * Drops the group's own show and edit pages from the cache. It runs before any invalidation, so a
   * page still open on a group that was just deleted does not refetch it and draw a 404.
   */
  function dropGroupPages(groupId: string): void {
    for (const scope of ["show", "edit"]) {
      queryClient.removeQueries({
        queryKey: [EVENT_GROUPS_QUERY_ROOT, scope, groupId],
      });
    }
  }

  function addEvents(
    groupId: string,
    eventIds: string[],
  ): Promise<GroupWriteResult> {
    return membership("post", groupId, eventIds);
  }

  function removeEvents(
    groupId: string,
    eventIds: string[],
  ): Promise<GroupWriteResult> {
    return membership("delete", groupId, eventIds);
  }

  async function membership(
    method: "post" | "delete",
    groupId: string,
    eventIds: string[],
  ): Promise<GroupWriteResult> {
    if (eventIds.length > MAX_IDS_PER_MEMBERSHIP_CALL) {
      return {
        ok: false,
        message: `Select at most ${MAX_IDS_PER_MEMBERSHIP_CALL} events at a time.`,
      };
    }
    breadcrumb(method === "post" ? "add-events" : "remove-events", {
      groupId,
      count: eventIds.length,
    });
    try {
      await admin.custom!({
        url: "",
        method,
        payload: { groupId, eventIds },
        meta: { urlPath: "event-groups/events" },
      });
    } catch (error) {
      const message = adminRefusal(error);
      if (message === null) throw error;
      return { ok: false, message };
    }
    invalidateGroups(groupId, true);
    return { ok: true };
  }

  /**
   * Deletes a group. A group with an `externalKey` was made by Tina's festival job and is unpublished on its festival's
   * page, never deleted here: a direct delete makes the job create the group again. It answers a sentence and calls
   * nothing.
   */
  async function remove(group: EventGroup): Promise<GroupWriteResult> {
    if (group.externalKey) {
      return {
        ok: false,
        message: "Tina made this group. Unpublish it on the festival's page.",
      };
    }
    breadcrumb("delete", { id: group.id });
    try {
      await admin.deleteOne!({ resource: "event-groups", id: group.id });
    } catch (error) {
      const message = adminRefusal(error);
      if (message === null) throw error;
      return { ok: false, message };
    }
    dropGroupPages(group.id);
    // The group's members lose their group, which their rows in the events list show.
    invalidateGroups(group.id, true);
    return { ok: true };
  }

  return { get, create, update, addEvents, removeEvents, remove };
};
